import { useRef } from "react";
import type { StickyCanvasObject } from "@refinement-canvas/domain";
import { useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useObjectTextEditor } from "../input/use-object-text-editor";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";

interface Props {
  readonly object: StickyCanvasObject;
}

// 付箋オブジェクト。
// - 配置位置に貼り付き、いつでも誰でも文字編集可能
// - サーバへは object.text.update op をキー入力後 50ms debounce で送信する
// - 単一クリックで選択、ダブルクリックで編集モードへ
export function StickyObject({ object }: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const isSelected = useCanvasStore((s) =>
    s.selectedObjectIds.length === 1 && s.selectedObjectIds[0] === object.id
  );

  const editor = useObjectTextEditor({
    objectId: object.id,
    initialText: object.text,
    elementRef: editorRef,
    dispatcher,
  });

  useObjectDrag({
    objectId: object.id,
    elementRef: containerRef,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialRotation: object.rotation,
    disabled: editor.isEditing,
  });

  return (
    <div
      ref={containerRef}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="sticky"
      onDoubleClick={editor.enterEditMode}
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        background: object.color ?? "#fff5b3",
        boxShadow: isSelected
          ? "0 0 0 2px var(--color-accent), 0 2px 6px rgba(0,0,0,0.15)"
          : "0 2px 6px rgba(0,0,0,0.15)",
        padding: "12px",
        fontSize: "14px",
        whiteSpace: "pre-wrap",
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        cursor: editor.isEditing ? "text" : "move",
        pointerEvents: "auto",
        userSelect: editor.isEditing ? "text" : "none",
      }}
    >
      <div
        ref={editorRef}
        data-testid={`canvas-object-text-${object.id}`}
        style={{
          width: "100%",
          height: "100%",
          outline: "none",
          cursor: "inherit",
        }}
        {...editor.editorProps}
      >
        {editor.isEditing ? null : object.text}
      </div>
    </div>
  );
}
